/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import {
	forwardRef,
	type ForwardRefExoticComponent,
	type RefAttributes,
	useCallback,
	useImperativeHandle,
	useRef,
} from 'react';

import { cssMap, jsx } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';

import { useThemeObserverAfterHydration } from '../utils/use-theme-observer-after-hydration';

export type VideoRendererHandle = {
	play: () => void;
	pause: () => void;
};

export type VideoRendererProps = {
	/**
	 * URL of the video file to render.
	 */
	videoSrc: string;
	/**
	 * Dark-mode video URL; used when active color mode is dark.
	 */
	videoSrcDark?: string;
	/**
	 * Poster image shown before the video loads.
	 */
	poster?: string;
	/**
	 * Accessible label for the rendered video.
	 */
	label: string;
	/**
	 * Id of an external element (e.g. a caption) to associate with the video
	 * via `aria-describedby`.
	 */
	describedById?: string;
	/**
	 * Fires on natural completion (media `ended`). Renderer also rewinds to
	 * frame 0. Not fired on user/browser-initiated pauses — wire `onPaused`
	 * to react to those.
	 */
	onEnded?: () => void;
	/**
	 * Fires when the browser pauses for any reason other than natural
	 * completion (explicit `pause()` call, or a src swap forcing a reload).
	 * Current time is preserved.
	 */
	onPaused?: () => void;
	/**
	 * Fires once with the video's intrinsic dimensions, after metadata loads.
	 * Used by the parent to honor `size="hug"`.
	 */
	onMetadataLoaded?: (dimensions: { width: number; height: number }) => void;
};

/* `objectFit: 'contain'` letterboxes rather than crops; use `size="hug"`
 * on `<MotionPlayer>` for a pixel-perfect fit. `backgroundColor` matches
 * the parent so letterbox bars read as chrome, not a broken player. */
const styles = cssMap({
	video: {
		width: '100%',
		height: '100%',
		objectFit: 'contain',
		backgroundColor: token('elevation.surface.sunken'),
		borderRadius: token('radius.xlarge'),
	},
});

/**
 * __VideoRenderer__
 *
 * `<video>`-backed renderer for `MotionPlayer`. Renders without native
 * controls; exposes `play`/`pause` via an imperative ref so the shared
 * `PlayPauseControl` can drive playback.
 */
const VideoRenderer: ForwardRefExoticComponent<
	VideoRendererProps & RefAttributes<VideoRendererHandle>
> = forwardRef<VideoRendererHandle, VideoRendererProps>(
	(
		{ videoSrc, videoSrcDark, poster, label, describedById, onEnded, onPaused, onMetadataLoaded },
		ref,
	) => {
		const videoRef = useRef<HTMLVideoElement>(null);
		/* A `<video src>` cannot be swapped in CSS, so the first client render keeps the
		 * prerendered light source and dark readers are switched over after mount. */
		const { colorMode = 'light' } = useThemeObserverAfterHydration();

		useImperativeHandle(
			ref,
			() => ({
				/* `play()` returns a promise that can reject on autoplay/abort.
				 * We swallow it — the user can just click play again. */
				play: () => void videoRef.current?.play(),
				pause: () => videoRef.current?.pause(),
			}),
			[],
		);

		const handleEnded = useCallback(() => {
			onEnded?.();
		}, [onEnded]);

		/* Forward non-natural pauses (explicit pause, theme-driven src
		 * reload, etc.) so the parent can reset `isPlaying`. Natural
		 * completion goes through `onEnded` instead. */
		const handlePause = useCallback(() => {
			/* Per the HTML media spec a `pause` event always fires immediately
			 * after `ended`. Guard against it via the live `ended` flag so
			 * `onPaused` is never called on natural completion. The rewind to
			 * frame 0 happens here (after the guard) so `ended` stays `true`
			 * when this handler runs — seeking would otherwise clear it. */
			if (videoRef.current?.ended) {
				videoRef.current.currentTime = 0;
				return;
			}
			onPaused?.();
		}, [onPaused]);

		const handleLoadedMetadata = useCallback(() => {
			const v = videoRef.current;
			if (v && onMetadataLoaded) {
				onMetadataLoaded({ width: v.videoWidth, height: v.videoHeight });
			}
		}, [onMetadataLoaded]);

		const activeSrc = colorMode === 'dark' && videoSrcDark ? videoSrcDark : videoSrc;

		return (
			<video
				ref={videoRef}
				css={styles.video}
				src={activeSrc}
				poster={poster}
				muted={true}
				playsInline={true}
				preload="metadata"
				onEnded={handleEnded}
				onPause={handlePause}
				onLoadedMetadata={handleLoadedMetadata}
				aria-label={label}
				aria-describedby={describedById}
			/>
		);
	},
);

VideoRenderer.displayName = 'VideoRenderer';

export default VideoRenderer;
